import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, ClockIcon, BookOpenIcon, WalletIcon, ShieldCheckIcon } from 'lucide-react';
const deliverableVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const deliverableItem = {
  hidden: {
    opacity: 0,
    x: -16
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: 'easeOut'
    }
  }
};
export function SolutionSection() {
  return <section className="px-4 py-20 md:py-28 bg-midnight-900/50" data-id="element-215">
      <div className="max-w-3xl mx-auto" data-id="element-216">
        {/* The Unique Mechanism */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-217">
          <div className="border-t-2 border-rescue-gold/40 w-16 mb-6" aria-hidden="true" data-id="element-218" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-219">
            The Unique Mechanism
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: 'easeOut'
      }} className="space-y-5 mb-16" data-id="element-220">
          <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-221">
            Introducing the{' '}
            <span className="text-rescue-gold font-bold" data-id="element-222">
              IRS Direct File Bypass
            </span>
            . This isn't a "hack"—it's a tactical map to the tools the
            government is legally required to provide for free. While the big
            companies spend millions to bury these links, this guide takes you
            straight to the IRS-owned portals that don't take credit cards
            because they don't charge fees. It's the "back door" to the IRS
            building that lets you skip the line and the bill.
          </p>
        </motion.div>

        {/* What You Get */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-223">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-10" data-id="element-224">
            What You Get
          </h3>
        </motion.div>

        <motion.div variants={deliverableVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="space-y-5" data-id="element-225">
          <motion.div variants={deliverableItem} className="flex items-start gap-5" data-id="element-226">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-227">
              <FileTextIcon className="w-5 h-5 text-rescue-gold" aria-hidden="true" data-id="element-228" />
            </div>
            <div data-id="element-229">
              <h4 className="font-body text-lg font-bold text-white mb-1" data-id="element-230">
                The $0 Tax Rescue Guide (PDF)
              </h4>
              <p className="font-body text-gray-400 leading-relaxed" data-id="element-231">
                The word-for-word, screen-by-screen map to the new 2024 IRS
                Direct File portal.
              </p>
            </div>
          </motion.div>

          <motion.div variants={deliverableItem} className="flex items-start gap-5" data-id="element-232">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-233">
              <ClockIcon className="w-5 h-5 text-rescue-gold" aria-hidden="true" data-id="element-234" />
            </div>
            <div data-id="element-235">
              <h4 className="font-body text-lg font-bold text-white mb-1" data-id="element-236">
                The 60-Minute Filing Sprint
              </h4>
              <p className="font-body text-gray-400 leading-relaxed" data-id="element-237">
                A tactical checklist to get your W-2s and 1099s entered with
                zero errors.
              </p>
            </div>
          </motion.div>

          <motion.div variants={deliverableItem} className="flex items-start gap-5" data-id="element-238">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-239">
              <BookOpenIcon className="w-5 h-5 text-rescue-gold" aria-hidden="true" data-id="element-240" />
            </div>
            <div data-id="element-241">
              <h4 className="font-body text-lg font-bold text-white mb-1" data-id="element-242">
                The "Plain English" Translation Key
              </h4>
              <p className="font-body text-gray-400 leading-relaxed" data-id="element-243">
                No more second-guessing what "Adjusted Gross Income" or "Nexus"
                means.
              </p>
            </div>
          </motion.div>

          <motion.div variants={deliverableItem} className="flex items-start gap-5" data-id="element-244">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-245">
              <WalletIcon className="w-5 h-5 text-rescue-gold" aria-hidden="true" data-id="element-246" />
            </div>
            <div data-id="element-247">
              <h4 className="font-body text-lg font-bold text-white mb-1" data-id="element-248">
                The "I Can't Pay" Protocol
              </h4>
              <p className="font-body text-gray-400 leading-relaxed" data-id="element-249">
                Exactly what to click if you owe money but have $0 in the bank
                (to avoid the 10x "Failure to File" penalty).
              </p>
            </div>
          </motion.div>

          <motion.div variants={deliverableItem} className="flex items-start gap-5" data-id="element-250">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-rescue-gold/10 border border-rescue-gold/30 flex items-center justify-center" data-id="element-251">
              <ShieldCheckIcon className="w-5 h-5 text-rescue-gold" aria-hidden="true" data-id="element-252" />
            </div>
            <div data-id="element-253">
              <h4 className="font-body text-lg font-bold text-white mb-1" data-id="element-254">
                The Audit-Proof Category Map
              </h4>
              <p className="font-body text-gray-400 leading-relaxed" data-id="element-255">
                The shortcut for gig workers to claim deductions without hunting
                for $10 receipts.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}